import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Play, Store } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { api, fmtErr } from "../lib/api";

const HERO_IMG = "https://images.pexels.com/photos/30003229/pexels-photo-30003229.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function DemoPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const enterDemo = async () => {
    setLoading(true);
    setError("");
    try {
      await api.post("/demo/bootstrap");
      await login("demo@tablediary.app", "TableDemo2026!");
      navigate("/dashboard");
    } catch (e) {
      setError(fmtErr(e));
      setLoading(false);
    }
  };

  return (
    <div data-testid="demo-page" className="dark relative min-h-screen flex items-center overflow-hidden">
      <img src={HERO_IMG} alt="The Demo Brasserie" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-black/60 to-black/30" />
      <div className="relative max-w-3xl mx-auto px-6 py-32">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="text-xs tracking-[0.3em] uppercase text-primary mb-6 [text-shadow:0_2px_12px_rgba(0,0,0,0.65)]">
          Live product tour
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="font-display text-4xl sm:text-6xl tracking-tight leading-none text-foreground">
          Step inside a running restaurant.
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
          className="mt-6 text-lg text-white/80 max-w-xl">
          The Demo Brasserie is fully stocked with bookings, tables, menus, events and guest CRM — explore the entire Business hub exactly as a venue owner would.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          className="mt-10 flex flex-wrap gap-4">
          <Button data-testid="enter-demo-btn" size="lg" className="rounded-full px-8" onClick={enterDemo} disabled={loading}>
            {loading ? <Loader2 className="mr-2 w-4 h-4 animate-spin" /> : <Play className="mr-2 w-4 h-4" />}
            {loading ? "Opening the demo..." : "Enter the live demo"}
          </Button>
          <Link to="/restaurants/demo-brasserie">
            <Button data-testid="demo-venue-btn" size="lg" variant="outline"
              className="rounded-full px-8 bg-black/30 backdrop-blur text-white border-white/40 hover:bg-white/10 hover:text-white">
              <Store className="mr-2 w-4 h-4" /> See the guest-facing page
            </Button>
          </Link>
        </motion.div>
        {error && <p data-testid="demo-error" className="mt-4 text-sm text-red-300">{error}</p>}
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
          className="mt-10 text-xs text-white/50">
          Read-only etiquette appreciated — it's a shared demo. Data resets periodically.
        </motion.p>
      </div>
    </div>
  );
}
